import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from "@angular/router";
import { EncryptDecryptService } from "../encrypt-decrypt.service";
import { UserProfile } from "../../models/user-profile";
import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userProfile!: UserProfile;
  private jwtHelper = new JwtHelperService();

  constructor(
    private router: Router,
    private httpClient: HttpClient,
    private encryptDecryptService: EncryptDecryptService
  ) {}

  handleError(error: HttpErrorResponse) {
    console.error('Auth error:', error);
    if (error.error instanceof ErrorEvent) {
      console.error('Client error occurred:', error.error.message);
    }
    return throwError(error);
  }

  register(payload: any): Observable<any> {
    return this.httpClient
      .post(`${environment.apiUrl}/public/register`, payload)
      .pipe(
        catchError(this.handleError)
      );
  }

  login(payload: any): Observable<any> {
    return this.httpClient.post<HttpResponse<UserProfile>>(
      `${environment.apiUrl}/public/login`,
      payload,
      { observe: 'response' }
    ).pipe(
      catchError(this.handleError)
    );
  }

  setUser(resp: any): void {
    const userProfile = resp.body;
    this.setSessionStorage('userProfile', userProfile);

    const authHeader = resp.headers.get('Authorization');
    if (authHeader && authHeader.startsWith('Bearer ')) {
      const token = authHeader.substring(7);
      this.setSessionStorage('token', token);
    } else {
      console.error('No valid Authorization header found');
    }

    this.setSessionStorage('permission', userProfile.role);
    this.setSessionStorage('username', userProfile.username);

    window.location.replace('./main');
  }

  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      return !this.jwtHelper.isTokenExpired(token);
    } catch (err) {
      console.error('Error validating token:', err);
      return false;
    }
  }

  getToken(): string | null {
    try {
      const encryptedToken = JSON.parse(sessionStorage.getItem('token') || 'null');
      if (!encryptedToken) return null;

      return this.encryptDecryptService.decrypt(encryptedToken);
    } catch (e) {
      console.error('Error retrieving token:', e);
      return null;
    }
  }

  logout(): void {
    sessionStorage.clear();
    this.router.navigate(['/login']);
  }

  setSessionStorage(key: string, value: any): void {
    const encryptedValue = this.encryptDecryptService.encrypt(JSON.stringify(value));
    sessionStorage.setItem(key, JSON.stringify(encryptedValue));
  }

  getSessionStorage(key: string): any {
    if (sessionStorage.getItem(key)) {
      try {
        const encrypted = JSON.parse(sessionStorage.getItem(key)!);
        const decrypted = this.encryptDecryptService.decrypt(encrypted);
        return JSON.parse(decrypted);
      } catch (e) {
        console.error(`Error decrypting session storage for key: ${key}`, e);
        return null;
      }
    }
    return null;
  }

  clearSessionStorage(key: string): void {
    sessionStorage.removeItem(key);
  }

  getUserProfile(): UserProfile {
    if (this.userProfile) {
      return this.userProfile;
    } else {
      const profile = this.getSessionStorage('userProfile');
      if (profile) {
        this.userProfile = profile;
        return profile;
      }
      return {} as UserProfile;
    }
  }
}
